import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import { getFormFieldProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import type { SelectOptionIF } from '@/usage/Usage.page';
import type { Datasource } from '@/search/search.types';
import { useTranslation } from 'react-i18next';
import type { TooltipPosition } from '@seeqdev/qomponents';
import { Icon, Select } from '@seeqdev/qomponents';
import { buildSelectOptions } from '@/search/SearchFilters.molecule';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { sqSearchStore } from '@/core/core.stores';
import clsx from 'clsx';

export interface DatasourceSelectIF extends ValidatingFormComponent<any> {
  component: 'DatasourceSelectFormComponent';
  onChange: (selectedDatasources: SelectOptionIF<Datasource>[] | SelectOptionIF<Datasource>) => void;
  placeholder: string;
  testId?: string;
  wrapperClasses?: string;
  label: string;
  isMultiple?: boolean;
  tooltip?: string;
  tooltipDelay?: number;
  tooltipPlacement?: TooltipPosition;
  labelClassNames?: string;
}

export const DatasourceSelectFormComponent: React.FunctionComponent<DatasourceSelectIF> = (props) => {
  const {
    name,
    validation,
    testId = 'datasourceSelect',
    wrapperClassNames,
    customErrorText,
    required = false,
    extendValidation,
    onChange,
    placeholder,
    label,
    isMultiple = true,
    tooltip,
    tooltipDelay,
    tooltipPlacement = 'top',
    labelClassNames,
  } = props;

  const { t } = useTranslation();

  const formState = useForm().getState();
  const showError =
    _.has(formState.errors, name) &&
    formState.hasValidationErrors &&
    ((formState.modified && formState.modified[name]) || formState.submitFailed);

  const defaultValidation = (value: any) => required && !value;
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  const { datasources, isUsingDatasourcePrefsSearchFilters } = useFlux(sqSearchStore);
  const datasourceOptions = buildSelectOptions(datasources, [], isUsingDatasourcePrefsSearchFilters);

  return (
    <>
      <FormFieldWrapper
        wrapperClassNames={clsx('flexFill', wrapperClassNames)}
        testId={testId}
        showError={showError}
        errorClassNames="ml20"
        customErrorText={customErrorText}>
        <Field name={name} validate={appliedValidation}>
          {({ input, meta }) => {
            const { value } = getFormFieldProps(formState, input, meta, props);
            const filteredDatasources = [...datasourceOptions].filter(({ label: datasourceLabel }) => {
              if (Array.isArray(value)) {
                const labelsToExclude = value.map(({ label }: { label: string }) => label);
                return !labelsToExclude.includes(datasourceLabel);
              }
              return true;
            });

            return (
              <div>
                <div data-testid="datasourceSelectInput" className="multiSelectLarge flexRowContainer flexFill">
                  <label className={`mb5 ${labelClassNames}`}> {t(label)}</label>
                  {tooltip && (
                    <Icon
                      icon="fa-question-circle"
                      large={true}
                      tooltip={t(tooltip)}
                      tooltipDelay={tooltipDelay}
                      tooltipPlacement={tooltipPlacement}
                      extraClassNames="pl5 cursorPointer mt5"
                    />
                  )}
                  <Select
                    isMulti={isMultiple}
                    isClearable={true}
                    closeMenuOnSelect={!isMultiple} // if it's a single select, close the menu on select
                    isSearchable={true}
                    options={filteredDatasources}
                    menuPortalTarget={document.body}
                    placeholder={t(placeholder)}
                    value={value}
                    showError={showError}
                    onChange={(selectedDatasources: SelectOptionIF<Datasource>[]) => {
                      onChange(selectedDatasources);
                    }}
                    extraClassNames="mt5"
                  />
                </div>
              </div>
            );
          }}
        </Field>
      </FormFieldWrapper>
    </>
  );
};
